import {useState} from "react";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {FormProviderRHF, useFormRHF} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {REGISTRATION_WIZARD_TEST_IDS} from "@pg-mono/test-ids";
import {EmailInputWithVerification} from "@pg-rp/email-with-verification";

import {useEmailVerificationGate} from "../../../email_verification/hooks/use_email_verification_gate";
import {validationMessages} from "../../../form/utils/validation_messages";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useRegistrationWizardEmailSignUp} from "../../hooks/use_registration_wizard_email_sign_up";
import {useRegistrationWizardTrackingContext} from "../../hooks/use_registration_wizard_tracking_context";
import {useSocialAuth} from "../../hooks/use_social_auth";
import {RegistrationMethod, setLastAuthAction, setRegistrationAuthMode, setRegistrationMethod} from "../../state/registration_flow_slice";
import {trackRegistrationWizardButtonClick} from "../../tracking/track_registration_wizard_events";
import {AuthActionType} from "../../types/AuthActionType";
import {RegistrationWizardSocialButtons} from "./RegistrationWizardSocialButtons";
import {RegistrationWizardTerms} from "./RegistrationWizardTerms";

interface IEmailFormValues {
    email: string;
}

const emailValidationSchema = Yup.object({
    email: Yup.string().trim().email(validationMessages.email).required(validationMessages.required)
});

export const RegistrationWizardEmailForm = () => {
    const dispatch = useAppDispatch();
    const flowState = useAppSelector((state) => state.registrationFlow);
    const trackingContext = useRegistrationWizardTrackingContext();
    const [isAwaitingNextStep, setIsAwaitingNextStep] = useState(false);

    const form = useFormRHF<IEmailFormValues>({
        defaultValues: {email: flowState.wizardDrafts.email ?? ""},
        resolver: yupResolver(emailValidationSchema),
        mode: "onChange"
    });
    const {isSubmitting, isValid} = form.formState;
    const {verificationTrigger, verificationData, isChecking, isEmailVerified} = useEmailVerificationGate({
        currentEmail: form.watch("email"),
        setFieldError: (message) => form.setError("email", {message}),
        bypassEmail: flowState.wizardDrafts.email
    });
    const isSubmitDisabled = !isValid || isSubmitting || isChecking || !isEmailVerified;

    const completeAuth = (authActionType: AuthActionType, method: RegistrationMethod) => {
        setIsAwaitingNextStep(true);
        dispatch(setRegistrationMethod({method}));

        if (authActionType === AuthActionType.REGISTRATION) {
            flowState.registrationSuccessCallback?.();
        }

        dispatch(setRegistrationAuthMode({authMode: authActionType === AuthActionType.REGISTRATION ? "registration" : "login"}));
        dispatch(setLastAuthAction({authAction: authActionType}));
    };

    const {startGoogleLogin, handleFacebookResponse} = useSocialAuth({
        onAuthSuccess: (authActionType) => completeAuth(authActionType, "social"),
        userSource: flowState.userSource ?? UserSource.REGISTRATION,
        gtmSourceSection: flowState.gtmSourceSection,
        amplitudePlacement: trackingContext.authPlacement,
        amplitudeLocation: trackingContext.location,
        authPlacement: trackingContext.authPlacement
    });

    const {submitEmail} = useRegistrationWizardEmailSignUp({
        setEmailError: (message) => form.setError("email", {message}),
        onSignedUp: () => completeAuth(AuthActionType.REGISTRATION, "email")
    });

    const onSubmit = (values: IEmailFormValues) => submitEmail(values.email);

    return (
        <FormProviderRHF {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                <fieldset disabled={isAwaitingNextStep} className={formStyle}>
                    <div className={fieldsStyle}>
                        <EmailInputWithVerification
                            verificationData={verificationData}
                            verificationTrigger={verificationTrigger}
                            placeholder="Podaj swój adres e-mail"
                            dataTestId={REGISTRATION_WIZARD_TEST_IDS.EMAIL_INPUT}
                        />

                        <div className={separatorStyle}>
                            <span className={separatorLineStyle} />
                            <Text as="span" variant="info_txt_1">
                                lub
                            </Text>
                            <span className={separatorLineStyle} />
                        </div>

                        <RegistrationWizardSocialButtons
                            onGoogleClick={() => {
                                trackRegistrationWizardButtonClick(trackingContext, "rp_auth_google");
                                startGoogleLogin();
                            }}
                            onFacebookClick={() => trackRegistrationWizardButtonClick(trackingContext, "rp_auth_facebook")}
                            onFacebookResponse={handleFacebookResponse}
                        />

                        <RegistrationWizardTerms />
                    </div>

                    <Button
                        type="submit"
                        variant="filled_primary"
                        fullWidth
                        isLoading={isSubmitting || isChecking || isAwaitingNextStep}
                        disabled={isSubmitDisabled}
                        dataTestId={REGISTRATION_WIZARD_TEST_IDS.EMAIL_SUBMIT_BUTTON}
                    >
                        DALEJ
                    </Button>
                </fieldset>
            </form>
        </FormProviderRHF>
    );
};

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 3,
    minWidth: 0,
    m: 0,
    p: 0,
    border: "none"
});

const fieldsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    md: {
        rowGap: 2
    }
});

const separatorStyle = css({
    display: "flex",
    alignItems: "center",
    columnGap: 2,
    color: "var(--colors-gray-800)",
    md: {
        color: "var(--colors-gray-700)"
    }
});

const separatorLineStyle = css({
    flex: "1 1 0",
    height: "1px",
    backgroundColor: "var(--colors-gray-300)"
});
import {useState} from "react";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import * as Yup from "yup";

import {Button} from "@pg-design/button-module";
import {FormProviderRHF, useFormRHF} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {REGISTRATION_WIZARD_TEST_IDS} from "@pg-mono/test-ids";
import {EmailInputWithVerification} from "@pg-rp/email-with-verification";

import {useEmailVerificationGate} from "../../../email_verification/hooks/use_email_verification_gate";
import {validationMessages} from "../../../form/utils/validation_messages";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {useRegistrationWizardEmailSignUp} from "../../hooks/use_registration_wizard_email_sign_up";
import {useRegistrationWizardTrackingContext} from "../../hooks/use_registration_wizard_tracking_context";
import {useSocialAuth} from "../../hooks/use_social_auth";
import {RegistrationMethod, setLastAuthAction, setRegistrationAuthMode, setRegistrationMethod} from "../../state/registration_flow_slice";
import {trackRegistrationWizardButtonClick} from "../../tracking/track_registration_wizard_events";
import {AuthActionType} from "../../types/AuthActionType";
import {RegistrationWizardSocialButtons} from "./RegistrationWizardSocialButtons";
import {RegistrationWizardTerms} from "./RegistrationWizardTerms";

interface IEmailFormValues {
    email: string;
}

const emailValidationSchema = Yup.object({
    email: Yup.string().trim().email(validationMessages.email).required(validationMessages.required)
});

export const RegistrationWizardEmailForm = () => {
    const dispatch = useAppDispatch();
    const flowState = useAppSelector((state) => state.registrationFlow);
    const trackingContext = useRegistrationWizardTrackingContext();
    const [isAwaitingNextStep, setIsAwaitingNextStep] = useState(false);

    const form = useFormRHF<IEmailFormValues>({
        defaultValues: {email: flowState.wizardDrafts.email ?? ""},
        resolver: yupResolver(emailValidationSchema),
        mode: "onChange"
    });
    const {isSubmitting, isValid} = form.formState;
    const {verificationTrigger, verificationData, isChecking, isEmailVerified} = useEmailVerificationGate({
        currentEmail: form.watch("email"),
        setFieldError: (message) => form.setError("email", {message}),
        bypassEmail: flowState.wizardDrafts.email
    });
    const isSubmitDisabled = !isValid || isSubmitting || isChecking || !isEmailVerified;

    const completeAuth = (authActionType: AuthActionType, method: RegistrationMethod) => {
        setIsAwaitingNextStep(true);
        dispatch(setRegistrationMethod({method}));

        if (authActionType === AuthActionType.REGISTRATION) {
            flowState.registrationSuccessCallback?.();
        }

        dispatch(setRegistrationAuthMode({authMode: authActionType === AuthActionType.REGISTRATION ? "registration" : "login"}));
        dispatch(setLastAuthAction({authAction: authActionType}));
    };

    const {startGoogleLogin, handleFacebookResponse} = useSocialAuth({
        onAuthSuccess: (authActionType) => completeAuth(authActionType, "social"),
        userSource: flowState.userSource ?? UserSource.REGISTRATION,
        gtmSourceSection: flowState.gtmSourceSection,
        amplitudePlacement: trackingContext.authPlacement,
        amplitudeLocation: trackingContext.location,
        authPlacement: trackingContext.authPlacement
    });

    const {submitEmail} = useRegistrationWizardEmailSignUp({
        setEmailError: (message) => form.setError("email", {message}),
        onSignedUp: () => completeAuth(AuthActionType.REGISTRATION, "email")
    });

    const onSubmit = (values: IEmailFormValues) => submitEmail(values.email);

    return (
        <FormProviderRHF {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} noValidate>
                <fieldset disabled={isAwaitingNextStep} className={formStyle}>
                    <div className={fieldsStyle}>
                        <EmailInputWithVerification
                            verificationData={verificationData}
                            verificationTrigger={verificationTrigger}
                            placeholder="Podaj swój adres e-mail"
                            dataTestId={REGISTRATION_WIZARD_TEST_IDS.EMAIL_INPUT}
                        />

                        <div className={separatorStyle}>
                            <span className={separatorLineStyle} />
                            <Text as="span" variant="info_txt_1">
                                lub
                            </Text>
                            <span className={separatorLineStyle} />
                        </div>

                        <RegistrationWizardSocialButtons
                            onGoogleClick={() => {
                                trackRegistrationWizardButtonClick(trackingContext, "rp_auth_google");
                                startGoogleLogin();
                            }}
                            onFacebookClick={() => trackRegistrationWizardButtonClick(trackingContext, "rp_auth_facebook")}
                            onFacebookResponse={handleFacebookResponse}
                        />

                        <RegistrationWizardTerms />
                    </div>

                    <Button
                        type="submit"
                        variant="filled_primary"
                        fullWidth
                        isLoading={isSubmitting || isChecking || isAwaitingNextStep}
                        disabled={isSubmitDisabled}
                        dataTestId={REGISTRATION_WIZARD_TEST_IDS.EMAIL_SUBMIT_BUTTON}
                    >
                        DALEJ
                    </Button>
                </fieldset>
            </form>
        </FormProviderRHF>
    );
};

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 3,
    minWidth: 0,
    m: 0,
    p: 0,
    border: "none"
});

const fieldsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1,
    md: {
        rowGap: 2
    }
});

const separatorStyle = css({
    display: "flex",
    alignItems: "center",
    columnGap: 2,
    color: "var(--colors-gray-800)",
    md: {
        color: "var(--colors-gray-700)"
    }
});

const separatorLineStyle = css({
    flex: "1 1 0",
    height: "1px",
    backgroundColor: "var(--colors-gray-300)"
});
